import { AxiosRequestConfig } from "axios";
import api from "./axiosConfig";
import kpiProdData from "../data/kpi-prod.json";

export interface MonthlyKpiStats {
  month: string;
  newUsers: number;
  totalUsers: number;
  activeUsers: number;
  uniquePurchasingUsers: number;
  transactionCount: number;
  totalTransactionAmount: number;
  totalAssignedCashback: number;
  uniqueDownsizingUsers: number;
  totalDownsizingAmount: number;
  totalGamesPlayed: number;
  totalGamesWon: number;
  totalGamesLost: number;
  totalRewardAmount: number;
  uniqueGamePlayers: number;
}

export interface AllTimeKpiStats {
  totalUsers: number;
  totalActiveUsers: number;
  totalUniquePurchasingUsers: number;
  totalTransactionCount: number;
  totalTransactionAmount: number;
  totalAssignedCashback: number;
  totalUniqueDownsizingUsers: number;
  totalDownsizingAmount: number;
  totalGamesPlayed: number;
  totalGamesWon: number;
  totalGamesLost: number;
  totalRewardAmount: number;
  totalUniqueGamePlayers: number;
}

export interface KpiActiveUsersComparison {
  month: string;
  v1: number;
  v2: number;
  source: "Ga4AllStreams" | "Ga4Web" | "Ga4App" | "Analytics";
}

export interface KpiReportResponse {
  monthlyStats: MonthlyKpiStats[];
  allTimeStats: AllTimeKpiStats;
  // Optional because the mock JSON lacks it. True when the backend serves the
  // corrected (V2) Active Users figures.
  activeUsersV2Enabled?: boolean;
  // V1 vs V2 Active Users per month. The backend sends it when the V2 flag is on
  // and null when it is off; absent in the mock JSON.
  activeUsersComparison?: KpiActiveUsersComparison[] | null;
}

export interface KpiReportV2Response {
  report: KpiReportResponse;
  activeUsersComparison: KpiActiveUsersComparison[];
}

export const getKpiReport = async (
  // The backend serves this from a cache it refreshes daily, but an instance that
  // has just started has not filled it yet and builds the report in the request.
  // The dashboard passes a timeout so it fails visibly instead of hanging; the
  // build finishes server-side regardless, so a reload gets the cached copy.
  config?: AxiosRequestConfig
): Promise<KpiReportResponse> => {
  if (process.env.REACT_APP_USE_KPI_MOCK === "true") {
    return kpiProdData as KpiReportResponse;
  }

  try {
    const response = await api.post("/business-account/get-kpi-report", {}, config);
    return response.data;
  } catch (error) {
    console.error("Getting KPI report error:", error);
    throw error;
  }
};

// SuperAdmin-only preview of the report with corrected Active Users, plus the
// V1 vs V2 comparison per month.
export const getKpiReportV2 = async (
  config?: AxiosRequestConfig
): Promise<KpiReportV2Response> => {
  try {
    const response = await api.post("/business-account/get-kpi-report-v2", {}, config);
    return response.data;
  } catch (error) {
    console.error("Getting KPI report V2 error:", error);
    throw error;
  }
};
